import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { PatientDataContext } from '../../../../context/PatientDataContext';

const SYMPTOMS = [
    'Inattention',
    'Difficulty concentrating or focusing',
    'Loses track of conversations/discussions',
    'Difficulty completing tasks',
    'Disorganised',
    'Procrastination/Avoidance',
    'Misplaces items',
    'Distractability',
    'Forgetfulness',
    'Fidgetiness',
    'Restlessness',
    'Difficulty staying still',
    'Difficulty remaining quiet',
    'Hyperactivity',
    'Difficulty pacing conversations',
    'Impulsive decisions/actions',
    'Impatience',
    'Interrupts others',
    'Excessive anxiety',
    'Uncontrolled worry',
    'Fatigue',
    'Physical tension',
    'Sleep difficulties',
    'Feeling down',
    'Anhedonia',
    'Weight/appetite changes',
    'Worthlessness/guilt',
    'Suicidal ideation',
    "Feeling afraid",
    'Becoming annoyed/irritable',
    'Easily overwhelmed',
    'Sensitive to criticism/rejection',
    'Low self-esteem',
    'Indecisiveness',    
    'Other'
];

// Mapping configuration for BAARS Current questionnaire
const BAARS_CURRENT_MAPPING = {
    'adhdScales_Patient_A1a_Adulthood': {
        symptom: 'Inattention',
        questionText: 'Fail to give close attention to details or make careless mistakes in my work'
    },
    'adhdScales_Patient_A2a_Adulthood': {
        symptom: 'Fidgetiness',
        questionText: 'Fidget with hands or feet or squirm'
    },
    'adhdScales_Patient_A1b_Adulthood': {
        symptom: 'Difficulty concentrating or focusing',
        questionText: 'Have difficulty sustaining my attention in tasks or fun activities'
    },
    'adhdScales_Patient_A2b_Adulthood': {
        symptom: 'Difficulty staying still',
        questionText: 'Leave my seat in situations in which sitting is expected'
    },
    'adhdScales_Patient_A1c_Adulthood': {
        symptom: 'Loses track of conversations/discussions',
        questionText: 'Don\'t listen when spoken to directly'
    },
    'adhdScales_Patient_A2c_Adulthood': {
        symptom: 'Restlessness',
        questionText: 'Feel restless'
    },
    'adhdScales_Patient_A1d_Adulthood': {
        symptom: 'Difficulty completing tasks',
        questionText: 'Don\'t follow through on instructions and fail to finish work'
    },
    'adhdScales_Patient_A2d_Adulthood': {
        symptom: 'Difficulty remaining quiet',
        questionText: 'Have difficulty engaging in leisure activities or doing fun things quietly'
    },
    'adhdScales_Patient_A1e_Adulthood': {
        symptom: 'Disorganised',
        questionText: 'Have difficulty organising tasks and activities'
    },
    'adhdScales_Patient_A2e_Adulthood': {
        symptom: 'Hyperactivity',
        questionText: 'Feel "on the go" or "driven by a motor"'
    },
    'adhdScales_Patient_A1f_Adulthood': {
        symptom: 'Procrastination/Avoidance',
        questionText: 'Avoid, dislike, or am reluctant to engage in work that requires sustained mental effort'
    },
    'adhdScales_Patient_A2f_Adulthood': {
        symptom: 'Difficulty pacing conversations',
        questionText: 'Talk excessively'
    },
    'adhdScales_Patient_A1g_Adulthood': {
        symptom: 'Misplaces items',
        questionText: 'Lose things necessary for tasks or activities'
    },
    'adhdScales_Patient_A2g_Adulthood': {
        symptom: 'Impulsive decisions/actions',
        questionText: 'Blurt out answers before questions have been completed'
    },
    'adhdScales_Patient_A1h_Adulthood': {
        symptom: 'Distractability',
        questionText: 'Easily distracted'
    },
    'adhdScales_Patient_A2h_Adulthood': {
        symptom: 'Impatience',
        questionText: 'Have difficulty waiting for your turn'
    },
    'adhdScales_Patient_A1i_Adulthood': {
        symptom: 'Forgetfulness',
        questionText: 'Forgetful in daily activities'
    },
    'adhdScales_Patient_A2i_Adulthood': {
        symptom: 'Interrupts others',
        questionText: 'Interrupt or intrude on others'
    }
};

// Mapping configuration for GAD-7 questionnaire
const GAD7_MAPPING = {
    'gad7_question1': {
        symptoms: ['Excessive anxiety'],
        questionText: 'Feeling nervous, anxious or on edge'
    },
    'gad7_question2': {
        symptoms: ['Uncontrolled worry'],
        questionText: 'Not being able to stop or control worrying'
    },
    'gad7_question3': {
        symptoms: ['Uncontrolled worry'],
        questionText: 'Worrying too much about different things'
    },
    'gad7_question4': {
        symptoms: ['Physical tension'],
        questionText: 'Trouble relaxing'
    },
    'gad7_question5': {
        symptoms: ['Restlessness', 'Difficulty staying still'],
        questionText: 'Being so restless that it is hard to sit still'
    },
    'gad7_question6': {
        symptoms: ['Becoming annoyed/irritable'],
        questionText: 'Becoming easily annoyed or irritable'
    },
    'gad7_question7': {
        symptoms: ['Feeling afraid'],
        questionText: 'Feeling afraid as if something awful might happen'
    }
};

// Mapping configuration for PHQ-9 questionnaire
const PHQ9_MAPPING = {
    'phq9_question1': {
        symptoms: ['Anhedonia'],
        questionText: 'Little interest or pleasure in doing things'
    },
    'phq9_question2': {
        symptoms: ['Feeling down'],
        questionText: 'Feeling down, depressed, or hopeless'
    },
    'phq9_question3': {
        symptoms: ['Sleep difficulties'],
        questionText: 'Trouble falling or staying asleep, or sleeping too much'
    },
    'phq9_question4': {
        symptoms: ['Fatigue'],
        questionText: 'Feeling tired or having little energy'
    },
    'phq9_question5': {
        symptoms: ['Weight/appetite changes'],
        questionText: 'Poor appetite or overeating'
    },
    'phq9_question6': {
        symptoms: ['Worthlessness/guilt'],
        questionText: 'Feeling bad about yourself – or that you are a failure or have let yourself or your family down'
    },
    'phq9_question7': {
        symptoms: ['Difficulty concentrating or focusing'],
        questionText: 'Trouble concentrating on things, such as reading the newspaper or watching television'
    },
    'phq9_question8': {
        symptoms: [], // Ignore this question
        questionText: 'Moving or speaking so slowly that other people could have noticed? Or the opposite – being so fidgety or restless that you have been moving around a lot more than usual'
    },
    'phq9_question9': {
        symptoms: ['Suicidal ideation'],
        questionText: 'Thoughts that you would be better off dead, or of hurting yourself in some way'
    }
};

export default function BupaLetterWizard({ open, onOpenChange, patientId, clinicianId }) {    

    const { patientDataModel } = useContext(PatientDataContext);
    const [selectedSymptoms, setSelectedSymptoms] = useState(new Set());
    const [otherSymptomText, setOtherSymptomText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [symptomEvidence, setSymptomEvidence] = useState({});
    const [expandedEvidence, setExpandedEvidence] = useState(new Set());

    const otherSymptomRef = useRef(null);
    
    // Helper function to get questionnaire response value
    const getQuestionnaireResponse = useCallback((formKey, varName) => {
        const formData = patientDataModel?.formData?.[formKey];
        if (!formData) return null;

        // Look through all sections and questions to find the variable
        for (const sectionKey in formData.formSubsections || {}) {
            const section = formData.formSubsections[sectionKey];
            for (const questionKey in section.questions || {}) {
                const question = section.questions[questionKey];
                for (const subquestionKey in question.subquestions || {}) {
                    const subquestion = question.subquestions[subquestionKey];
                    if (subquestion.serviceUserVarName === varName) {
                        return subquestion.answer?.value || subquestion.answer;
                    }
                }
            }
        }
        return null;
    }, [patientDataModel]);

    // Gather all evidence for all symptoms
    const gatherSymptomEvidence = useCallback(() => {
        const evidence = {};
        
        // Initialize evidence for all symptoms
        SYMPTOMS.forEach(symptom => {
            evidence[symptom] = [];
        });

        // Process BAARS Current responses
        Object.entries(BAARS_CURRENT_MAPPING).forEach(([varName, { symptom, questionText }]) => {
            const response = getQuestionnaireResponse('baarsIVCurrent', varName);
            if (response && response !== 'No response') {
                const isQualifying = response === 'Often' || response === 'Very Often';
                evidence[symptom].push({
                    questionnaire: 'BAARS-IV',
                    question: questionText,
                    response,
                    isQualifying
                });
            }
        });

        // Process GAD-7 responses
        Object.entries(GAD7_MAPPING).forEach(([varName, { symptoms, questionText }]) => {
            const response = getQuestionnaireResponse('gad7', varName);
            if (response && response !== 'No response') {
                const isQualifying = response === 'More than half the days' || response === 'Nearly every day';
                symptoms.forEach(symptom => {
                    evidence[symptom].push({
                        questionnaire: 'GAD-7',
                        question: questionText,
                        response,
                        isQualifying
                    });
                });
            }
        });

        // Process PHQ-9 responses
        Object.entries(PHQ9_MAPPING).forEach(([varName, { symptoms, questionText }]) => {
            if (symptoms.length === 0) return; // Skip PHQ-9 question 8
            const response = getQuestionnaireResponse('phq9', varName);
            if (response && response !== 'No response') {
                // Special handling for suicide question (question 9) - any response other than "Not at all" is qualifying
                const isSuicideQuestion = varName === 'phq9_question9';
                const isQualifying = isSuicideQuestion 
                    ? response !== 'Not at all'
                    : (response === 'More than half the days' || response === 'Nearly every day');
                
                symptoms.forEach(symptom => {
                    evidence[symptom].push({
                        questionnaire: 'PHQ-9',
                        question: questionText,
                        response,
                        isQualifying
                    });
                });
            }
        });

        return evidence;
    }, [getQuestionnaireResponse]);

    // Get symptoms ordered by evidence strength
    const getOrderedSymptoms = () => {
        const evidence = gatherSymptomEvidence();
        
        // Separate "Other" and "Suicidal ideation" from the rest
        const symptomsWithoutSpecial = SYMPTOMS.filter(s => s !== 'Other' && s !== 'Suicidal ideation');
        const otherSymptom = SYMPTOMS.filter(s => s === 'Other');
        const suicidalIdeation = SYMPTOMS.filter(s => s === 'Suicidal ideation');
        
        // Check if Suicidal ideation has qualifying evidence
        const suicidalHasQualifyingEvidence = evidence['Suicidal ideation']?.some(e => e.isQualifying) || false;
        
        // Separate symptoms with evidence from those without
        const symptomsWithEvidence = [];
        const symptomsWithoutEvidence = [];
        
        symptomsWithoutSpecial.forEach(symptom => {
            const hasEvidence = evidence[symptom]?.length > 0;
            if (hasEvidence) {
                symptomsWithEvidence.push(symptom);
            } else {
                symptomsWithoutEvidence.push(symptom);
            }
        });
        
        // Sort symptoms with evidence by strength
        const orderedSymptomsWithEvidence = symptomsWithEvidence.sort((a, b) => {
            const aQualifyingCount = evidence[a]?.filter(e => e.isQualifying).length || 0;
            const bQualifyingCount = evidence[b]?.filter(e => e.isQualifying).length || 0;
            const aTotalCount = evidence[a]?.length || 0;
            const bTotalCount = evidence[b]?.length || 0;
            
            // First sort by qualifying evidence count (descending)
            if (aQualifyingCount !== bQualifyingCount) {
                return bQualifyingCount - aQualifyingCount;
            }
            
            // Then by total evidence count (descending)
            if (aTotalCount !== bTotalCount) {
                return bTotalCount - aTotalCount;
            }
            
            // Finally alphabetically
            return a.localeCompare(b);
        });
        
        // Sort symptoms without evidence alphabetically
        const orderedSymptomsWithoutEvidence = symptomsWithoutEvidence.sort((a, b) => a.localeCompare(b));
        
        // Build final order
        const finalOrder = [];
        
        // 1. Suicidal ideation first if it has qualifying evidence
        if (suicidalHasQualifyingEvidence) {
            finalOrder.push(...suicidalIdeation);
        }
        
        // 2. Other symptoms with evidence (ordered by evidence strength)
        finalOrder.push(...orderedSymptomsWithEvidence);
        
        // 3. Suicidal ideation if it doesn't have qualifying evidence (above symptoms with no evidence)
        if (!suicidalHasQualifyingEvidence) {
            finalOrder.push(...suicidalIdeation);
        }
        
        // 4. Symptoms without evidence (alphabetical)
        finalOrder.push(...orderedSymptomsWithoutEvidence);
        
        // 5. "Other" always at the bottom
        finalOrder.push(...otherSymptom);
        
        return finalOrder;
    };

    // Toggle symptom selection
    const toggleSymptom = (symptom) => {
        setSelectedSymptoms(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(symptom)) {
                newSelected.delete(symptom);
            } else {
                newSelected.add(symptom);
                
                // If selecting "Other", focus the inline input
                if (symptom === 'Other') {
                    setTimeout(() => {
                        if (otherSymptomRef.current) {
                            const input = otherSymptomRef.current.querySelector('input');
                            if (input) {
                                input.focus();
                            }
                        }
                    }, 100); // Short delay to allow button state to update
                }
            }
            return newSelected;
        });
    };

    // Toggle evidence expansion for a symptom
    const toggleEvidenceExpansion = (symptom) => {
        setExpandedEvidence(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(symptom)) {
                newExpanded.delete(symptom);
            } else {
                newExpanded.add(symptom);
            }
            return newExpanded;
        });
    };

    // Initialize with auto-preselections and evidence when opened
    useEffect(() => {
        if (open && patientDataModel) {
            const evidence = gatherSymptomEvidence();
            setSymptomEvidence(evidence);
            
            // Auto-preselect symptoms with qualifying evidence
            const autoSelected = new Set();
            Object.entries(evidence).forEach(([symptom, evidenceList]) => {
                const hasQualifyingEvidence = evidenceList.some(e => e.isQualifying);
                if (hasQualifyingEvidence) {
                    autoSelected.add(symptom);
                }
            });
            
            setSelectedSymptoms(autoSelected);
        } else if (!open) {
            // Reset when closed
            setSelectedSymptoms(new Set());
            setSymptomEvidence({});
            setExpandedEvidence(new Set());
            setOtherSymptomText('');
        }
    }, [open, patientDataModel, gatherSymptomEvidence]);

    // Check if form is valid for submission
    const isFormValid = () => {
        const selectedArray = Array.from(selectedSymptoms);
        
        // If "Other" is selected, require text in the otherSymptomText field
        if (selectedSymptoms.has('Other') && (!otherSymptomText || otherSymptomText.trim() === '')) {
            return false;
        }

        // Otherwise, just require at least one symptom to be selected
        return selectedArray.length > 0;
    };

    const handleCreateLetter = async () => {
        if (!isFormValid()) return;

        try {
            setIsSubmitting(true);

            // Convert selected symptoms Set to Array and format
            const selectedArray = Array.from(selectedSymptoms);
            const formattedSymptoms = selectedArray.flatMap(symptom => {
                if (symptom === 'Other') {
                    return [otherSymptomText.trim()]; // Add the other text instead of "Other"
                }
                return [symptom];
            });

            // Prepare data for cloud function
            const data = {
                patientId,
                clinicianId,
                symptoms: formattedSymptoms
            };

            console.log('Sending data to cloud function:', data);

            // Call the cloud function
            const generateBupaLetter = httpsCallable(functions, 'generateBupaLetter');
            
            // Make the API call
            const result = await generateBupaLetter(data);
            console.log('Cloud function response:', result.data);

            // Reset state and close dialog
            setSelectedSymptoms(new Set());
            setSymptomEvidence({});
            setExpandedEvidence(new Set());
            setOtherSymptomText('');
            onOpenChange(false);

        } catch (error) {
            console.error('Error creating Bupa letter:', error);
            // You could add error handling UI here if needed
        } finally {
            setIsSubmitting(false);
        }
    };



    // Render the comprehensive symptom table
    const renderSymptomTable = () => {
        const orderedSymptoms = getOrderedSymptoms();

    return (
            <div>
                {/* Instructional Text */}
                {/* <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                    <p className="text-sm text-blue-900 mb-2">
                        <strong>Symptom Selection:</strong> Below are all available symptoms ordered by supporting evidence from the patient's questionnaire responses.
                    </p>
                    <p className="text-xs text-blue-700">
                        • Click symptom badges to select/unselect them for the BUPA letter<br/>
                        • <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-medium">Blue badges</span> indicate strong responses (Often/Very Often, More than half the days/Nearly every day)<br/>
                        • <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-medium">Gray badges</span> indicate weaker responses (Never/Rarely, Sometimes)<br/>
                        • <strong>Special note:</strong> For suicidal ideation, any response other than "Not at all" is treated as strong evidence
                    </p>
                </div> */}

                {/* Symptoms Grid */}
                <div className="flex gap-1">
                    {/* Column 1 */}
                    <div className="flex-1 space-y-1">
                        {orderedSymptoms.filter((_, index) => index % 2 === 0).map((symptom) => {
                            const evidence = symptomEvidence[symptom] || [];
                            const isSelected = selectedSymptoms.has(symptom);
                            const isOther = symptom === 'Other';
                            const isExpanded = expandedEvidence.has(symptom);
                            
                            return (
                                <div key={symptom} className="border border-gray-200 rounded-md overflow-hidden">
                                    <div className={`p-2 ${evidence.length > 0 ? 'hover:bg-gray-50' : ''}`}>
                                        <div className="flex items-center gap-1">
                                            {/* Symptom Badge - Special handling for "Other" */}
                                            {isOther ? (
                                                <div className={`flex items-center gap-2 transition-all duration-300 ${
                                                    isSelected 
                                                        ? 'w-full bg-violet-600 text-white shadow-sm' 
                                                        : 'w-auto bg-white text-violet-600 border border-violet-200 hover:bg-violet-50'
                                                } px-3 py-1.5 rounded-md`}>
                                                    {/* Radio/Check Icon */}
                                                    <button
                                                        onClick={() => toggleSymptom(symptom)}
                                                        className="flex items-center gap-2 shrink-0"
                                                    >
                                                        <div className={`flex items-center justify-center w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                                                            isSelected 
                                                                ? 'border-white bg-white' 
                                                                : 'border-violet-300'
                                                        }`}>
                                                            {isSelected && (
                                                                <svg 
                                                                    className="w-3 h-3 text-violet-600" 
                                                                    fill="currentColor" 
                                                                    viewBox="0 0 20 20"
                                                                >
                                                                    <path 
                                                                        fillRule="evenodd" 
                                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                                                        clipRule="evenodd" 
                                                                    />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        {!isSelected && <span className="text-sm font-semibold">Other</span>}
                                                    </button>
                                                    
                                                    {/* Inline Input for "Other" */}
                                                    {isSelected && (
                                                        <div className="flex-grow" ref={otherSymptomRef}>
                                                            <input
                                                                type="text"
                                                                placeholder="Specify other symptom..."
                                                                className="w-full bg-transparent text-white placeholder-violet-200 text-sm font-semibold border-0 outline-none p-0"
                                                                value={otherSymptomText}
                                                                onChange={(e) => setOtherSymptomText(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => toggleSymptom(symptom)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-all duration-300 ${
                                                        isSelected
                                                            ? 'bg-violet-600 text-white shadow-sm'
                                                            : 'bg-white text-violet-600 border border-violet-200 hover:bg-violet-50'
                                                    }`}
                                                >
                                                    {/* Radio/Check Icon */}
                                                    <div className={`flex items-center justify-center w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                                                        isSelected 
                                                            ? 'border-white bg-white' 
                                                            : 'border-violet-300'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg 
                                                                className="w-3 h-3 text-violet-600" 
                                                                fill="currentColor" 
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path 
                                                                    fillRule="evenodd" 
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                                                    clipRule="evenodd" 
                                                                />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span>{symptom}</span>
                                                </button>
                                            )}
                                            
                                            {/* Evidence Toggle */}
                                            {evidence.length > 0 && (
                                                <button
                                                    onClick={() => toggleEvidenceExpansion(symptom)}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                                >
                                                    <svg 
                                                        className={`w-4 h-4 transition-transform duration-200 ${
                                                            isExpanded ? 'rotate-180' : ''
                                                        }`} 
                                                        fill="none" 
                                                        viewBox="0 0 24 24" 
                                                        stroke="currentColor"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Expanded Evidence Details */}
                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                        isExpanded && evidence.length > 0 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}>
                                        <div className="px-2 py-2 bg-gray-50 border-t border-gray-100">
                                            <div className="space-y-2">
                                                {evidence
                                                    .sort((a, b) => {
                                                        // Sort by qualifying evidence first (true before false)
                                                        if (a.isQualifying !== b.isQualifying) {
                                                            return b.isQualifying - a.isQualifying;
                                                        }
                                                        // Then by questionnaire order (BAARS, GAD-7, PHQ-9)
                                                        const questionnaireOrder = ['BAARS-IV', 'GAD-7', 'PHQ-9'];
                                                        return questionnaireOrder.indexOf(a.questionnaire) - questionnaireOrder.indexOf(b.questionnaire);
                                                    })
                                                    .map((item, index) => (
                                                        <div key={index} className="text-sm flex items-start gap-3">
                                                            <span className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${
                                                                item.isQualifying 
                                                                    ? 'bg-blue-100 text-blue-800' 
                                                                    : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                {item.response}
                                                            </span>
                                                            <span className="text-sm text-gray-700 leading-relaxed">
                                                                to "{item.question}" <strong>({item.questionnaire})</strong>
                                                            </span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Column 2 */}
                    <div className="flex-1 space-y-1">
                        {orderedSymptoms.filter((_, index) => index % 2 === 1).map((symptom) => {
                            const evidence = symptomEvidence[symptom] || [];
                            const isSelected = selectedSymptoms.has(symptom);
                            const isOther = symptom === 'Other';
                            const isExpanded = expandedEvidence.has(symptom);
                            
                            return (
                                <div key={symptom} className="border border-gray-200 rounded-md overflow-hidden">
                                    <div className={`p-2 ${evidence.length > 0 ? 'hover:bg-gray-50' : ''}`}>
                                        <div className="flex items-center gap-1">
                                            {/* Symptom Badge - Special handling for "Other" */}
                                            {isOther ? (
                                                <div className={`flex items-center gap-2 transition-all duration-300 ${
                                                    isSelected 
                                                        ? 'w-full bg-violet-600 text-white shadow-sm' 
                                                        : 'w-auto bg-white text-violet-600 border border-violet-200 hover:bg-violet-50'
                                                } px-3 py-1.5 rounded-md`}>
                                                    {/* Radio/Check Icon */}
                                                    <button
                                                        onClick={() => toggleSymptom(symptom)}
                                                        className="flex items-center gap-2 shrink-0"
                                                    >
                                                        <div className={`flex items-center justify-center w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                                                            isSelected 
                                                                ? 'border-white bg-white' 
                                                                : 'border-violet-300'
                                                        }`}>
                                                            {isSelected && (
                                                                <svg 
                                                                    className="w-3 h-3 text-violet-600" 
                                                                    fill="currentColor" 
                                                                    viewBox="0 0 20 20"
                                                                >
                                                                    <path 
                                                                        fillRule="evenodd" 
                                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                                                        clipRule="evenodd" 
                                                                    />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        {!isSelected && <span className="text-sm font-semibold">Other</span>}
                                                    </button>
                                                    
                                                    {/* Inline Input for "Other" */}
                                                    {isSelected && (
                                                        <div className="flex-grow" ref={otherSymptomRef}>
                                                            <input
                                                                type="text"
                                                                placeholder="Specify other symptom..."
                                                                className="w-full bg-transparent text-white placeholder-violet-200 text-sm font-semibold border-0 outline-none p-0"
                                                                value={otherSymptomText}
                                                                onChange={(e) => setOtherSymptomText(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => toggleSymptom(symptom)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-all duration-300 ${
                                                        isSelected
                                                            ? 'bg-violet-600 text-white shadow-sm'
                                                            : 'bg-white text-violet-600 border border-violet-200 hover:bg-violet-50'
                                                    }`}
                                                >
                                                    {/* Radio/Check Icon */}
                                                    <div className={`flex items-center justify-center w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                                                        isSelected 
                                                            ? 'border-white bg-white' 
                                                            : 'border-violet-300'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg 
                                                                className="w-3 h-3 text-violet-600" 
                                                                fill="currentColor" 
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path 
                                                                    fillRule="evenodd" 
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                                                    clipRule="evenodd" 
                                                                />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span>{symptom}</span>
                                                </button>
                                            )}
                                            
                                            {/* Evidence Toggle */}
                                            {evidence.length > 0 && (
                                                <button
                                                    onClick={() => toggleEvidenceExpansion(symptom)}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                                >
                                                    <svg 
                                                        className={`w-4 h-4 transition-transform duration-200 ${
                                                            isExpanded ? 'rotate-180' : ''
                                                        }`} 
                                                        fill="none" 
                                                        viewBox="0 0 24 24" 
                                                        stroke="currentColor"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Expanded Evidence Details */}
                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                        isExpanded && evidence.length > 0 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}>
                                        <div className="px-2 py-2 bg-gray-50 border-t border-gray-100">
                                            <div className="space-y-2">
                                                {evidence
                                                    .sort((a, b) => {
                                                        // Sort by qualifying evidence first (true before false)
                                                        if (a.isQualifying !== b.isQualifying) {
                                                            return b.isQualifying - a.isQualifying;
                                                        }
                                                        // Then by questionnaire order (BAARS, GAD-7, PHQ-9)
                                                        const questionnaireOrder = ['BAARS-IV', 'GAD-7', 'PHQ-9'];
                                                        return questionnaireOrder.indexOf(a.questionnaire) - questionnaireOrder.indexOf(b.questionnaire);
                                                    })
                                                    .map((item, index) => (
                                                        <div key={index} className="text-sm flex items-start gap-3">
                                                            <span className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${
                                                                item.isQualifying 
                                                                    ? 'bg-blue-100 text-blue-800' 
                                                                    : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                {item.response}
                                                            </span>
                                                            <span className="text-sm text-gray-700 leading-relaxed">
                                                                to "{item.question}" <strong>({item.questionnaire})</strong>
                                                            </span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
                    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] w-[95vw] max-w-[1000px] bg-white rounded-lg shadow-lg p-4 z-50 overflow-hidden flex flex-col">
                        
                        {/* Header */}
                        <div className="mb-4">
                            <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-1">
                                Select symptoms to include in the BUPA letter
                            </AlertDialog.Title>
                            <AlertDialog.Description className="text-gray-600 text-sm">
                                Some symptoms are pre-selected based on the patient's questionnaire responses. Please review these selections before creating the letter. Click the chevrons to see supporting evidence where this is available.
                            </AlertDialog.Description>
                        </div>

                        {/* Content */}
                        <div className="flex-grow overflow-y-auto mb-4" style={{ maxHeight: '70vh' }}>
                            {renderSymptomTable()}
                        </div>

                        {/* Summary and Actions */}
                        <div className="pt-3 border-t border-gray-200">
                            {/* Summary */}
                            <div className="mb-3 px-3 py-2 bg-gray-50 rounded-md">
                                <div className="text-sm text-gray-700">
                                    <span className="font-medium">{selectedSymptoms.size}</span> symptoms selected for BUPA letter
                                    {selectedSymptoms.has('Other') && otherSymptomText && (
                                        <span className="ml-2 text-xs text-gray-500">
                                            (including: "{otherSymptomText.substring(0, 50)}{otherSymptomText.length > 50 ? '...' : ''}")
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-2 font-semibold">
                                <AlertDialog.Cancel className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm">
                                Cancel
                            </AlertDialog.Cancel>
                            <AlertDialog.Action
                                onClick={handleCreateLetter}
                                disabled={isSubmitting || !isFormValid()}
                                    className={`px-6 py-2 bg-violet-600 text-white rounded-md transition-colors text-sm ${
                                        isSubmitting || !isFormValid()
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-violet-700'
                                    }`}
                            >
                                    {isSubmitting ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating Letter...
                                        </span>
                                    ) : (
                                        'Create Letter'
                                    )}
                            </AlertDialog.Action>
                            </div>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </>
    );
}